import type { Account, License } from '@ps/web-core-store';
import { format, isAfter, isBefore, isEqual, parseISO, subYears } from 'date-fns';
import debounce from 'debounce-promise';
import { validate as validateEmail } from 'email-validator';
import type { FieldState, FormApi } from 'final-form';
import { isPossiblePhoneNumber } from 'libphonenumber-js';
import { get, isEmpty, isNumber, isUndefined } from 'lodash';
import type { Required } from 'utility-types';

import { request as apiRequest } from '@swf/core/api/request';
import { Site } from '@swf/core/enums';
import type { StatusCode, StatusSubcode } from '@swf/core/enums/statusCode';
import type { RequestError } from '@swf/core/errors/RequestError';
import { getState, select } from '@swf/core/store';
import type { RequestOptions } from '@swf/core/types/Request';
import type { ErrorCollection } from '@swf/core/types/Response';
import type { TrackedTranslation, Values } from '@swf/core/types/i18n';

import { mapErrorCodes } from '../../swf-first/utils/cashier';

import type { DateOrPartial, PartialPhone } from './formatting';
import {
  TXTCLI_Email_address_is_not_valid,
  TXTCLI_Incorrect_Information_Entered,
  TXTCLI_Must_be_at_least_N0_characters_ELL,
  TXTCLI_PO_Box_addresses_are_not_permi_ELL,
  TXTCLI_Password_may_not_start_with_th_ELL,
  TXTCLI_Please_try_againX,
  TXTCLI_Required,
  TXTCLI_The_SSN_that_you_have_entered__ELL,
  TXTCLI_User_ID_XN0X_is_not_availlable_ELL,
  TXTCLI_Your_name_and_date_of_birth_is_ELL_1,
  TXTWCL_Check_thatXs_correct_and_try_a_ELL,
  TXTWCL_Does_not_include_N0,
  TXTWCL_Invalid_phone_number,
  TXTWCL_Must_be_shorter_than_N0_charac_ELL,
  TXTWCL_Must_contain_at_least_3_of_the_ELL,
  TXTWCL_Must_contain_at_least_one_digi_ELL,
  TXTWCL_Must_not_start_or_end_with_a_s_ELL,
  TXTWCL_Please_enter_a_valid_postcode__ELL,
  TXTWCL_Please_enter_phone_number,
  TXTWCL_Please_enter_valid_Personal_Nu_ELL,
  TXTWCL_Please_enter_valid_Uniform_Civ_ELL,
  TXTWCL_SorryX_something_went_wrongX,
  TXTWCL_SorryX_you_need_to_be_N0_or_ol_ELL,
  TXTWCL_Special_characters_are_not_all_ELL,
  TXTWCL_The_address_you_have_entered_i_ELL_1,
  TXTWCL_The_following_characters_are_n_ELL,
  TXTWCL_Try_refreshing_the_pageX,
  TXTWCL_Unable_to_validate_usernameX,
  TXTWCL_Your_UserID_must_not_contain_s_ELL
} from './validators.messages.json';

interface ValidatorMapPayload {
  account: Partial<Account>;
  isRealMoneyOK: boolean;
}

export interface ValidatorTranslation extends TrackedTranslation {
  values?: Values;
}

export interface Error {
  valid?: boolean | RegExp;
  message?: ValidatorTranslation | ValidatorTranslation[];
  value?: string;
  suggestedNames?: string[];
  code?: StatusCode | StatusSubcode;
}

export type ErrorOrPromise = Error | Promise<Error>;

export type Errors = Error[];

/* eslint-disable @typescript-eslint/no-explicit-any */
export type Validator<T = ErrorOrPromise> = (
  value?: any,
  allValues?: any,
  meta?: FieldState<any>
) => T;

export type Validators = Validator[];

type ValidatorFunction = (
  compareValue?: number,
  message?: Error['message'],
  allowEmpty?: boolean
) => (value: any) => Error;
/* eslint-enable @typescript-eslint/no-explicit-any */

export const NAME_AND_CITY_NOT_ALLOWED_SYMBOLS = /[.\\~`!@#$£±%^&*()_+={}/?|:;"<>,\][]/;
export const ADDRESS_NOT_ALLOWED_SYMBOLS = /[\\~`!@$£±%^*_={}?|:;"<>\][]/;
export const WHITE_START_OR_END_REGEXP = /(?:^\s+\S*)|(?:\S*\s+$)/;
export const START_WITH_LETTER_REGEXP = /^[A-Za-z]/;
export const CONTAIN_UPPERCASE_LETTER_REGEXP = /[A-Z]/;
export const CONTAIN_LOWERCASE_LETTER_REGEXP = /[a-z]/;
export const CONTAIN_SYMBOL_REGEXP = /[^A-Za-z\s\d\w]/;
export const CONTAIN_NUMBER_REGEXP = /[0-9]/;
export const NUMBERS_OR_SPACE = /^(?=.\d[\d]+$)/;
export const NUMBERS_ONLY_REGEXP = /^\d+$/;
export const NUMBERS_ONLY_DECIMALS_ALLOWED = /^\d+(\.\d{1,2})?$/;
export const START_WITH_BANNED_WORDS =
  /^(host|moderator|Accts|Cnslt|CSR|DAD|FPP|Host|HR|IT|Mgmt|Mktg|Pmts|Soft|Staff|Support|psdeadbl|Tilter|CAGE|DGE)/i;
export const PO_BOX_ADDRESS_REGEX = /(\bp[.\s\\/]*[box]+[.\s]*\b)|\bbx\b|\bbox\b/i;
export const CONTAIN_SPECIAL_CHARACTER_REGEXP = /[^\x20-\x7F]+/;
export const CONTAIN_LATIN_CHARACTERS_ONLY = /[^\x20-\x7FÀ-ÿ]+/;
export const CONTAIN_INT_AND_DEC_NUMBERS = /^[+-]?\d+(\.\d+)?$/;
export const RESTRICTED_SYMBOLS_SHORT = /[{}<>=]/;
export const SPACE_BETWEEN_CHAR_AND_INT = /\d[^\d\s]|[^\d\s]\d/;
export const CANADIAN_ZIP_CODE_FORMAT = /[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/;
export const containsOnlyEnglishCharacters = (value: string) =>
  !CONTAIN_SPECIAL_CHARACTER_REGEXP.test(value);
export const USER_ID_FRANCE_REGEX = /[^A-Za-z0-9+_/]+/;
export const mustContainIntAndDecNumbers =
  (message?: Error['message']) =>
  (value: string): Error => ({
    valid: !!value && CONTAIN_INT_AND_DEC_NUMBERS.test(value),
    message
  });

export const mustContainOnlyEnglishCharacters =
  () =>
  (value: string): Error => ({
    valid: !!value && containsOnlyEnglishCharacters(value),
    message: TXTWCL_Special_characters_are_not_all_ELL
  });

export const mustContainOnlyLatinCharacters =
  () =>
  (value: string): Error => ({
    valid: !CONTAIN_LATIN_CHARACTERS_ONLY.test(value),
    message: TXTWCL_Special_characters_are_not_all_ELL
  });

export const zipCodeFormatCAON =
  () =>
  (value: string): Error => ({
    valid: CANADIAN_ZIP_CODE_FORMAT.test(value),
    message: TXTWCL_Please_enter_a_valid_postcode__ELL
  });

export const mustNotContainSymbols =
  (regexStr: RegExp, message?: Error['message']) =>
  (value: string): Error => ({
    valid: !regexStr.test(value),
    message
  });

export const required =
  (message: Error['message'] | null = TXTCLI_Required) =>
  (value: string) => ({
    valid: !!value,
    message: message !== null ? message : undefined
  });

export const requiredNumber =
  (message: Error['message'] = TXTCLI_Required) =>
  (value: string) => ({
    valid: isNumber(value),
    message
  });

export const minLength =
  (min: number, message?: Error['message']) =>
  (value: string): Error => ({
    valid: !!value && value.length >= min,
    message
  });

export const maxLength =
  (
    max: number,
    message: Error['message'] = {
      ...TXTWCL_Must_be_shorter_than_N0_charac_ELL,
      values: { 0: max + 1 }
    },
    allowEmpty = false
  ) =>
  (value: string): Error => {
    const valid = value?.length <= max;

    return {
      valid: allowEmpty ? !value || valid : valid,
      message
    };
  };

export const lengthRange =
  (min: number, max: number, message?: Error['message']) =>
  (value: string): Error => ({
    valid: value?.length >= min && value?.length <= max,
    message
  });

export const allowedLength =
  (ranges: number[], message?: Error['message']) =>
  (value: string): Error => ({
    valid: ranges.includes(value?.length),
    message
  });

// All rework comments related to implict any comparison string to numbers with triple equal e.t.c.
// Also don't repeat yourself use helper funcions to extract common functionality.
// TODO: rework
export const minNumber =
  (min: number, message?: Error['message'], allowEmpty = false, allowDecimals = false) =>
  (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any
  ): Error => {
    const valid =
      (!!value || value === 0) && allowDecimals
        ? parseFloat(value) >= min
        : parseInt(value, 10) >= min;

    return {
      valid: allowEmpty ? isNaN(value) || valid : valid,
      message
    };
  };

// TODO: rework
export const maxNumber =
  (max: number, message?: Error['message'], allowEmpty = false, allowDecimals = false) =>
  (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any
  ): Error => {
    const lowerThanMax = allowDecimals ? parseFloat(value) <= max : parseInt(value, 10) <= max;
    const valid = (!!value || value === 0) && lowerThanMax;

    return {
      valid: allowEmpty ? isNaN(value) || valid : valid,
      message
    };
  };

// TODO: rework
export const numberRange =
  (min: number, max: number, message?: Error['message'], allowEmpty = false) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (value: any): Error => {
    const valid =
      minNumber(min, message, allowEmpty)(value).valid &&
      maxNumber(max, message, allowEmpty)(value).valid;

    return {
      valid: allowEmpty ? isNaN(value) || valid : valid,
      message
    };
  };

export const mustStartWithLetter =
  (message?: Error['message']) =>
  (value: string): Error => ({
    valid: !!value && START_WITH_LETTER_REGEXP.test(value),
    message
  });

export const mustContainNumber =
  (message?: Error['message']) =>
  (value: string): Error => ({
    valid: !!value && CONTAIN_NUMBER_REGEXP.test(value),
    message
  });

export const mustNotContainNumber =
  (message?: Error['message'], allowEmpty = false) =>
  (value: string): Error => {
    const valid = !!value && !CONTAIN_NUMBER_REGEXP.test(value);
    return {
      valid: allowEmpty ? !value || valid : valid,
      message
    };
  };

export const mustNotIncludeFieldValue =
  (fieldName: string) =>
  (inputValue: string | undefined, allValues: Record<string, string>): Error => ({
    valid:
      !allValues[fieldName] ||
      !inputValue ||
      !inputValue.toLowerCase().includes(allValues[fieldName].toLowerCase()),
    message: { ...TXTWCL_Does_not_include_N0, values: { 0: allValues[fieldName] } }
  });

export const mustContainUpperCaseLetter =
  (message?: Error['message']) =>
  (value: string): Error => ({
    valid: !!value && CONTAIN_UPPERCASE_LETTER_REGEXP.test(value),
    message
  });

export const mustContainLowerCaseLetter =
  (message?: Error['message']) =>
  (value: string): Error => ({
    valid: !!value && CONTAIN_LOWERCASE_LETTER_REGEXP.test(value),
    message
  });

export const mustContainSymbol =
  (message?: Error['message']) =>
  (value: string): Error => ({
    valid: !!value && CONTAIN_SYMBOL_REGEXP.test(value),
    message
  });

export const mustContainSpaceBetweenIntAndChar =
  (message?: Error['message']) =>
  (value: string): Error => ({
    valid: !!value && !SPACE_BETWEEN_CHAR_AND_INT.test(value),
    message
  });

export const mustNotContainName = (
  { firstName = '', lastName = '' }: Partial<Account>,
  message?: Error['message']
) => {
  const indexOfSecondLastName = lastName.lastIndexOf(' ');
  const names =
    indexOfSecondLastName === -1
      ? [lastName]
      : [
          lastName.substring(indexOfSecondLastName + 1),
          lastName.substring(0, indexOfSecondLastName)
        ];
  names.push(firstName);

  return composeValidatorsWithThreshold(
    [
      ...names.map((name) => mustNotContain(name, message)),
      ...names.map((name) => mustNotContain(name.split(/[\s|-]+/).join(''), message))
    ],
    message
  );
};

export const composeValidatorsWithThreshold =
  (validators: Validator[], message?: Error['message'], threshold?: number) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (value: any): Error => ({
    valid:
      validators.reduce(
        (passed, validator) => ((validator(value) as Error).valid ? ++passed : passed),
        0
      ) >= (threshold || validators.length),
    message
  });

const validatorMap: {
  [field in 'password' | 'userId' | 'address']: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [license in Site | License | 'default']?: any;
  };
} = {
  password: {
    [Site.ES]: (payload?: ValidatorMapPayload) => {
      const validations = [
        composeValidatorsWithThreshold(
          [
            mustContainLowerCaseLetter(),
            mustContainUpperCaseLetter(),
            mustContainNumber(),
            mustContainSymbol()
          ],
          TXTWCL_Must_contain_at_least_3_of_the_ELL,
          3
        ),
        mustContainOnlyEnglishCharacters()
      ];

      if (payload?.isRealMoneyOK) {
        const { dob } = payload.account;
        validations.push(
          composeValidatorsWithThreshold(
            [
              mustNotContain(dob && format(parseISO(dob), 'ddLLyy')),
              mustNotContainName(payload.account)
            ],
            TXTCLI_Your_name_and_date_of_birth_is_ELL_1
          )
        );
      }

      return validations;
    },
    [Site.DE]: (payload?: ValidatorMapPayload) => {
      const validations = [
        composeValidatorsWithThreshold(
          [
            mustContainLowerCaseLetter(),
            mustContainUpperCaseLetter(),
            mustStartWithLetter(),
            mustContainNumber(TXTWCL_Must_contain_at_least_one_digi_ELL),
            mustContainSymbol()
          ],
          TXTWCL_Must_contain_at_least_3_of_the_ELL,
          3
        ),
        mustContainOnlyEnglishCharacters()
      ];

      if (payload?.isRealMoneyOK) {
        const { dob } = payload.account;
        validations.push(
          composeValidatorsWithThreshold(
            [
              mustNotContain(dob && format(parseISO(dob), 'ddLLyy')),
              mustNotContainName(payload.account)
            ],
            TXTCLI_Your_name_and_date_of_birth_is_ELL_1
          )
        );
      }

      return validations;
    },
    default: [mustContainNumber(TXTWCL_Must_contain_at_least_one_digi_ELL)]
  },
  userId: {
    [Site.ES]: () => [mustContainOnlyEnglishCharacters()],
    default: []
  },
  address: {
    [Site.USNJ]: () => [poBoxAddressNotAllowed],
    [Site.CZ]: () => [
      mustNotContainSymbols(ADDRESS_NOT_ALLOWED_SYMBOLS, TXTCLI_Incorrect_Information_Entered)
    ],
    default: [
      mustNotContainSymbols(
        ADDRESS_NOT_ALLOWED_SYMBOLS,
        TXTWCL_The_address_you_have_entered_i_ELL_1
      )
    ]
  }
};

export const getValidatorsForLicense = (
  license: Site | License,
  field: keyof typeof validatorMap,
  payload?: ValidatorMapPayload
) =>
  validatorMap[field][license]
    ? validatorMap[field][license](payload)
    : validatorMap[field].default;

export const mustNotContain =
  (value: string | undefined, message?: Error['message']) =>
  (inputValue: string | undefined): Error => ({
    valid: !value || !inputValue || !inputValue.toLowerCase().includes(value.toLowerCase()),
    message
  });

export const mustNotContainField =
  (field: string, message?: Error['message']) =>
  (value: string, allValues: Record<string, string>): Error =>
    mustNotContain(allValues[field], message)(value);

export const mustStartWithField =
  (field: string, message?: Error['message']) =>
  (value: string, allValues: Record<string, string>): Error => ({
    valid:
      !value || !allValues[field] || value.toLowerCase().startsWith(allValues[field].toLowerCase()),
    message
  });

export const numbersOnly =
  (message?: Error['message'], allowEmpty = false, allowDecimals = false) =>
  (value: string): Error => {
    const valid =
      !!value && allowDecimals
        ? NUMBERS_ONLY_DECIMALS_ALLOWED.test(value)
        : NUMBERS_ONLY_REGEXP.test(value);
    return {
      valid: allowEmpty ? !value || valid : valid,
      message
    };
  };

export const numbersOrSpace =
  (message?: Error['message'], allowEmpty = false) =>
  (value: string): Error => {
    const valid = !!value && NUMBERS_OR_SPACE.test(value);
    return {
      valid: allowEmpty ? !value || valid : valid,
      message
    };
  };

export const email = (value: string): Error => ({
  valid: !!value && validateEmail(value) && !RESTRICTED_SYMBOLS_SHORT.test(value),
  message: TXTCLI_Email_address_is_not_valid
});

export const poBoxAddressNotAllowed = (value: string): Error => ({
  valid: !PO_BOX_ADDRESS_REGEX.test(value),
  message: TXTCLI_PO_Box_addresses_are_not_permi_ELL
});

export const noWhiteStartOrEnd = (value: string): Error => ({
  valid: !WHITE_START_OR_END_REGEXP.test(value),
  message: TXTWCL_Must_not_start_or_end_with_a_s_ELL
});

export const equalToField =
  (field: string, message?: Error['message']) =>
  (value: string, allValues: Record<string, string>) => ({
    valid: get(allValues, field) === value,
    message
  });

export const notEqualTo = (compareTo: string, message?: Error['message']) => (value: string) => ({
  valid: compareTo !== value,
  message
});

export const greaterThan = (compareTo: number, message?: Error['message']) => (value: number) => ({
  valid: value > compareTo,
  message
});

export const compareWithNatNumber = (value: string) => {
  const natNum = Number(select.account.getNationalDocumentNumber(getState()));

  if (!natNum) return { valid: true };

  if (Number(value) !== natNum) {
    return {
      valid: false,
      message: [TXTCLI_The_SSN_that_you_have_entered__ELL, TXTCLI_Please_try_againX]
    };
  }

  return {
    valid: true
  };
};

export const mustStartWith =
  (value: string | undefined, message?: Error['message']) =>
  (inputValue: string | undefined): Error => ({
    valid: !value || !inputValue || inputValue.startsWith(value),
    message
  });

const validateDate =
  (preposition: 'equalOrBefore' | 'equalOrAfter') =>
  (compare?: string | number, message?: Error['message']) =>
  (value: string | number): Error => {
    let valid = false;
    if (!compare) {
      valid = true;
    } else {
      const valueDate = new Date(value);
      const compareDate = new Date(compare);
      valid =
        isEqual(valueDate, compareDate) ||
        (preposition === 'equalOrBefore'
          ? isBefore(valueDate, compareDate)
          : isAfter(valueDate, compareDate));
    }
    return { valid, message };
  };

const validateDateComparedWith =
  (
    compareDateProperty: string,
    message: Error['message'],
    validatorFunction: ValidatorFunction
  ): Validator =>
  (value, allValues) =>
    validatorFunction(allValues && allValues[compareDateProperty], message)(value);

export const equalOrBeforeDate = validateDate('equalOrBefore');

export const equalOrAfterDate = validateDate('equalOrAfter');

export const equalOrBeforeDateComparedWith = (
  compareDateProperty: string,
  message?: Error['message']
) => validateDateComparedWith(compareDateProperty, message, equalOrBeforeDate);

export const equalOrAfterDateComparedWith = (
  compareDateProperty: string,
  message?: Error['message']
) => validateDateComparedWith(compareDateProperty, message, equalOrAfterDate);

export const checkUserPassword = (value: string): Error => {
  const invalid = value?.startsWith('tmp');

  return {
    valid: !invalid,
    message: invalid ? TXTCLI_Password_may_not_start_with_th_ELL : undefined
  };
};

export const checkUserEmail = (
  options: Required<Partial<RequestOptions>, 'meta'>,
  installId: string | undefined,
  message: Error['message']
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let request: any;
  let prevValue: string;

  const checkUserEmailAsync: Validator = async (value: string): Promise<Error> => {
    if (!validateEmail(value)) {
      return {
        valid: false
      };
    }

    if (value !== prevValue) {
      request = apiRequest({
        ...options,
        service: '/api/v1-preview/registration/email/validate',
        method: 'POST',
        http: true,
        body: { email: value, installId }
      });
    }
    prevValue = value;

    try {
      await request;
      return { valid: true };
    } catch (err) {
      return {
        valid: false,
        message
      };
    }
  };

  return debounce(checkUserEmailAsync, 1000) as Validator;
};

export const getCheckUserId = (
  options: Required<Partial<RequestOptions>, 'meta'>,
  staticValidators?: Validator<Error>[],
  site?: Site
) => {
  const suggestedNamesCache = Object.create(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let request: any;
  let prevValue: string;

  const checkUserIdAsync: Validator = async (value: string): Promise<Error> => {
    const unableToValidateUsername: Error = {
      valid: false,
      message: { ...TXTWCL_Unable_to_validate_usernameX, values: { 0: value } }
    };
    if (!containsOnlyEnglishCharacters(value)) {
      return unableToValidateUsername;
    }
    if (START_WITH_BANNED_WORDS.test(value)) {
      return {
        valid: false,
        message: { ...TXTCLI_User_ID_XN0X_is_not_availlable_ELL, values: { 0: value } }
      };
    }

    if (staticValidators) {
      const validationResult = staticValidators.find(
        (staticValidator) => !staticValidator(value).valid
      );
      if (validationResult) {
        return validationResult(value);
      }
    }

    if (!value || value.length < 4 || WHITE_START_OR_END_REGEXP.test(value)) {
      return { valid: true, suggestedNames: [] };
    }

    if (value in suggestedNamesCache) {
      return {
        valid: false,
        message: { ...TXTCLI_User_ID_XN0X_is_not_availlable_ELL, values: { 0: value } },
        suggestedNames: suggestedNamesCache[value]
      };
    }

    if (USER_ID_FRANCE_REGEX.test(value)) {
      return {
        valid: false,
        message: TXTWCL_Your_UserID_must_not_contain_s_ELL
      };
    }

    if (value !== prevValue) {
      request = apiRequest({
        ...options,
        service: '/api/v1-preview/registration/userId/validate',
        method: 'POST',
        http: true,
        body: { userId: value }
      });
    }
    prevValue = value;

    try {
      const {
        body: { available, suggestedNames = [] }
      } = await request;

      if (!available) {
        // eslint-disable-next-line require-atomic-updates
        suggestedNamesCache[value] = suggestedNames;
        return {
          valid: false,
          message: { ...TXTCLI_User_ID_XN0X_is_not_availlable_ELL, values: { 0: value } },
          suggestedNames
        };
      }
      return { valid: true };
    } catch (err) {
      const { headers } = err as RequestError;
      return {
        ...unableToValidateUsername,
        code: headers?.statusSubcode
      };
    }
  };

  return debounce(checkUserIdAsync, 510); // debounce to match WMT velocity settings of 2 in 2 sec
};

interface DateDOBMessage {
  invalidDateMessage?: TrackedTranslation;
  legalAgeMessage?: TrackedTranslation;
}

export const dateDob =
  ({
    invalidDateMessage = TXTWCL_Check_thatXs_correct_and_try_a_ELL,
    legalAgeMessage = TXTWCL_SorryX_you_need_to_be_N0_or_ol_ELL
  }: DateDOBMessage) =>
  (value: DateOrPartial): Error => {
    const legalAge = select.cms.getLegalAge(getState());

    if (!legalAge) {
      return {
        valid: false,
        message: [TXTWCL_SorryX_something_went_wrongX, TXTWCL_Try_refreshing_the_pageX]
      };
    }

    if (!(value instanceof Date)) {
      return {
        valid: false,
        message: invalidDateMessage
      };
    }

    const today = new Date();

    if (isBefore(value, subYears(today, 110)) || isAfter(value, today)) {
      return {
        valid: false,
        message: invalidDateMessage
      };
    }

    if (isAfter(value, subYears(today, legalAge))) {
      return {
        valid: false,
        message: { ...legalAgeMessage, values: { 0: legalAge } }
      };
    }

    return {
      valid: true
    };
  };

export const phone =
  (minLength: number, required = true) =>
  (value: PartialPhone): Error => {
    if (isUndefined(value) || isEmpty(value?.area) || isEmpty(value)) {
      if (!required) return { valid: true };

      return {
        valid: false,
        message: TXTWCL_Please_enter_phone_number
      };
    }

    const phoneNumber = Object.values(value).join('');
    if (phoneNumber.length < minLength) {
      return {
        valid: false,
        message: { ...TXTCLI_Must_be_at_least_N0_characters_ELL, values: { 0: minLength } }
      };
    }

    return {
      valid: true
    };
  };

export const validateUCN =
  (dob: string) =>
  (value: string): Error => {
    if (!value || !dob) return { valid: true };
    if (value.length < 10)
      return { valid: false, message: TXTWCL_Please_enter_valid_Uniform_Civ_ELL };
    const DOBDataType = new Date(dob);
    const year = DOBDataType.getFullYear();
    const dobValue =
      year.toString().slice(-2) +
      (DOBDataType.getMonth() + (year < 1900 ? 21 : year > 1999 ? 41 : 1))
        .toString()
        .padStart(2, '0') +
      DOBDataType.getDate().toString().padStart(2, '0');
    if (value.substring(0, 6) !== dobValue) {
      return { valid: false, message: TXTWCL_Please_enter_valid_Uniform_Civ_ELL };
    }
    const mod =
      value
        .substring(0, value.length - 1)
        .split('')
        .map((char, index) => +char * [2, 4, 8, 5, 10, 9, 7, 3, 6][index])
        .reduce((a, b) => a + b) % 11;

    const isValid = +value.slice(-1) === (!mod || mod === 10 ? 0 : mod);
    if (!isValid) return { valid: false, message: TXTWCL_Please_enter_valid_Uniform_Civ_ELL };
    return { valid: true };
  };

export const validatePNF = (value: string): Error => {
  if (!value) return { valid: true };
  if (value.length < 10)
    return { valid: false, message: TXTWCL_Please_enter_valid_Personal_Nu_ELL };
  const isValid =
    +value.slice(-1) ===
    value
      .substring(0, value.length - 1)
      .split('')
      .map((char, index) => +char * [21, 19, 17, 13, 11, 9, 7, 3, 1][index])
      .reduce((a, b) => a + b, 0) %
      10;

  if (!isValid) return { valid: false, message: TXTWCL_Please_enter_valid_Personal_Nu_ELL };
  return { valid: true };
};

export const phoneRMF =
  (code: string | undefined, required = true) =>
  (value: string): Error => {
    const phoneNumber = '+' + code + value;
    const isValidNumber = isPossiblePhoneNumber(phoneNumber);

    if (isUndefined(value) || isEmpty(value)) {
      if (!required) return { valid: true };
      return {
        valid: false,
        message: TXTWCL_Please_enter_phone_number
      };
    }

    if (value && !isValidNumber) {
      return {
        valid: false,
        message: TXTWCL_Invalid_phone_number
      };
    }

    return {
      valid: true
    };
  };

export const validateAllowedCharacters =
  (validator: RegExp, validCharacters: RegExp) => (value: string) => ({
    valid: !!value && validator.test(value),
    message: {
      ...TXTWCL_The_following_characters_are_n_ELL,
      values: { 0: value?.replace(validCharacters, ' ') }
    }
  });

/* eslint-disable @typescript-eslint/no-explicit-any */
export const serverFieldErrMsgWarning =
  (error: ErrorCollection | null, removeWizardError: () => void, form: FormApi): Validator =>
  (value: string, values: any, meta?: FieldState<any>) => {
    if (meta?.data) {
      if (value && error?.fieldErrors?.length) {
        const index = error.fieldErrors.findIndex(({ fieldName }) => meta.name.includes(fieldName));

        if (value === meta.initial && meta.data && index > -1) {
          const { code, defaultMessage } = error.fieldErrors[index];
          const message = defaultMessage || mapErrorCodes(code || '');
          if (!meta.data.warning && message) {
            form?.mutators?.setFieldData(meta.name, {
              warning: message
            });
            meta.focus();
          }
          if (!meta.data.samePageError) {
            removeWizardError();
          }

          return { valid: true };
        }
      }

      delete meta.data.warning;
    }

    return { valid: true };
  };
/* eslint-enable @typescript-eslint/no-explicit-any */
